import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import {
  loadCreativeChats,
  deleteCreativeChat,
  forkCreativeChat,
  buildChatTree,
  flattenChatTree,
  filterAndSortChats,
  getChatTags,
  toggleChatPin,
  toggleChatArchive
} from '../services/creativeChatsService';
import { LayoutGrid, List, Trash2, GitFork, ChevronRight, ChevronDown, Pin, Archive } from 'lucide-react';
import ConfirmationModal from '../components/ConfirmationModal';
import ChatFilters from '../components/saved-chats/ChatFilters';
import ForkTreeModal from '../components/saved-chats/ForkTreeModal';

export default function SavedChats() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { selectedBrand } = useApp();
  const [chats, setChats] = useState([]);
  const [chatTree, setChatTree] = useState([]);
  const [flatChats, setFlatChats] = useState([]); // Flattened list with childCount
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('masonry'); // 'masonry' or 'list'
  const [chatToDelete, setChatToDelete] = useState(null);
  const [expandedChats, setExpandedChats] = useState(new Set()); // Collapsed by default

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [forkDepthFilter, setForkDepthFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [showPinnedOnly, setShowPinnedOnly] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [forkTreeChat, setForkTreeChat] = useState(null); // Chat to show fork tree for

  useEffect(() => {
    loadChats();
  }, [currentUser, selectedBrand]);

  // Build tree and flat list whenever chats change
  useEffect(() => {
    if (chats.length > 0) {
      const tree = buildChatTree(chats);
      setChatTree(tree);
      // Create flattened list with childCount for filtering
      const flat = flattenChatTree(tree);
      setFlatChats(flat);
      // Keep forks collapsed by default (don't auto-expand)
    } else {
      setChatTree([]);
      setFlatChats([]);
    }
  }, [chats]);

  // Load available tags
  useEffect(() => {
    const loadTags = async () => {
      if (!currentUser) return;
      try {
        const brandId = selectedBrand?.id || null;
        const tags = await getChatTags(currentUser.uid, brandId);
        setAvailableTags(tags);
      } catch (err) {
        console.error('Error loading tags:', err);
      }
    };
    loadTags();
  }, [currentUser, selectedBrand, chats]);

  // Filtered and sorted chats
  const filteredChats = useMemo(() => {
    return filterAndSortChats(flatChats, {
      searchQuery,
      tags: selectedTags,
      forkDepthFilter,
      showArchived,
      showPinnedOnly,
      sortBy
    });
  }, [flatChats, searchQuery, selectedTags, forkDepthFilter, showArchived, showPinnedOnly, sortBy]);

  const loadChats = async () => {
    if (!currentUser) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Filter by brand if one is selected (server-side)
      const brandId = selectedBrand?.id || null;
      const loadedChats = await loadCreativeChats(currentUser.uid, brandId);

      console.log('Loaded chats:', loadedChats.length, 'chats');
      if (loadedChats.length > 0) {
        console.log('First chat sample:', {
          id: loadedChats[0].id,
          title: loadedChats[0].title,
          thumbnail: loadedChats[0].thumbnail,
          brandName: loadedChats[0].brandName,
          brandId: loadedChats[0].brandId
        });
      }

      // Filter out chats with invalid thumbnail URLs (relative paths)
      const chatsWithValidThumbnails = loadedChats.map(chat => {
        if (chat.thumbnail && !chat.thumbnail.startsWith('http://') && !chat.thumbnail.startsWith('https://')) {
          console.warn('Chat has relative thumbnail URL, setting to null:', chat.id, chat.thumbnail);
          return { ...chat, thumbnail: null };
        }
        return chat;
      });

      setChats(chatsWithValidThumbnails);
    } catch (err) {
      console.error('Error loading chats:', err);
      setError('Failed to load saved chats');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChatClick = (chatId) => {
    navigate(`/conversational?chatId=${chatId}`);
  };

  const handleNewChat = () => {
    navigate('/conversational');
  };

  const handleDeleteClick = (chatId, e) => {
    e.stopPropagation(); // Prevent navigation when clicking delete
    setChatToDelete(chatId);
  };

  const handleConfirmDelete = async () => {
    if (!chatToDelete) return;

    try {
      await deleteCreativeChat(chatToDelete);
      // Refresh the chat list
      loadChats();
    } catch (err) {
      console.error('Error deleting chat:', err);
      setError('Failed to delete chat. Please try again.');
    } finally {
      setChatToDelete(null);
    }
  };

  const handleForkClick = async (chatId, e) => {
    e.stopPropagation();
    try {
      const newChatId = await forkCreativeChat(chatId, null, currentUser.uid);
      navigate(`/conversational?chatId=${newChatId}`);
    } catch (err) {
      console.error('Error forking chat:', err);
      setError('Failed to fork chat. Please try again.');
    }
  };

  const handlePinClick = async (chatId, e) => {
    e.stopPropagation();
    try {
      await toggleChatPin(chatId, currentUser.uid);
      // Optimistically update local state
      setChats(prev => prev.map(chat =>
        chat.id === chatId ? { ...chat, isPinned: !chat.isPinned } : chat
      ));
    } catch (err) {
      console.error('Error toggling pin:', err);
      setError('Failed to toggle pin. Please try again.');
    }
  };

  const handleArchiveClick = async (chatId, e) => {
    e.stopPropagation();
    try {
      await toggleChatArchive(chatId, currentUser.uid);
      // Optimistically update local state
      setChats(prev => prev.map(chat =>
        chat.id === chatId ? { ...chat, isArchived: !chat.isArchived } : chat
      ));
    } catch (err) {
      console.error('Error toggling archive:', err);
      setError('Failed to toggle archive. Please try again.');
    }
  };

  // Find the root of a fork tree and show the modal
  const handleShowForkTree = (chatId, e) => {
    e.stopPropagation();
    // Find the chat in the tree
    const findChatInTree = (nodes, id) => {
      for (const node of nodes) {
        if (node.id === id) return node;
        if (node.children && node.children.length > 0) {
          const found = findChatInTree(node.children, id);
          if (found) return found;
        }
      }
      return null;
    };

    // If this chat has forks, show its tree
    const chat = findChatInTree(chatTree, chatId);
    if (chat) {
      // Find the root of this chat's tree
      const findRoot = (nodes, targetId, visited = new Set()) => {
        for (const node of nodes) {
          if (visited.has(node.id)) continue;
          visited.add(node.id);

          // Check if this node is the root of our target's tree
          const containsTarget = (n, id) => {
            if (n.id === id) return true;
            if (n.children) {
              return n.children.some(c => containsTarget(c, id));
            }
            return false;
          };

          if (containsTarget(node, targetId)) {
            return node;
          }
        }
        return null;
      };

      const rootNode = findRoot(chatTree, chatId);
      if (rootNode) {
        setForkTreeChat(rootNode);
      }
    }
  };

  const toggleExpand = (chatId, e) => {
    e.stopPropagation();
    setExpandedChats(prev => {
      const next = new Set(prev);
      if (next.has(chatId)) {
        next.delete(chatId);
      } else {
        next.add(chatId);
      }
      return next;
    });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown date';

    // Handle Firestore Timestamp
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInMs = now - date;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) {
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      if (diffInHours === 0) {
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        return diffInMinutes <= 1 ? 'Just now' : `${diffInMinutes} minutes ago`;
      }
      return diffInHours === 1 ? '1 hour ago' : `${diffInHours} hours ago`;
    } else if (diffInDays === 1) {
      return 'Yesterday';
    } else if (diffInDays < 7) {
      return `${diffInDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Render flat chat card (grid view - no nesting)
  const renderFlatChatCard = (chat) => {
    const isForked = !!chat.forkedFromChatId;
    const hasForks = chat.childCount > 0;

    return (
      <div
        key={chat.id}
        onClick={() => handleChatClick(chat.id)}
        style={{
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-4)',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          backgroundColor: 'var(--color-surface)',
          overflow: 'hidden',
          position: 'relative',
          borderLeft: isForked ? '3px solid var(--primary-400)' : chat.isPinned ? '3px solid var(--warning-500)' : '1px solid var(--color-border)',
          opacity: chat.isArchived ? 0.7 : 1
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'var(--primary-400)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
          e.currentTarget.style.transform = 'translateY(-2px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = isForked || chat.isPinned ? 'var(--primary-400)' : 'var(--color-border)';
          e.currentTarget.style.boxShadow = 'none';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        {/* Status badges */}
        <div style={{
          display: 'flex',
          gap: 'var(--space-1)',
          marginBottom: chat.isPinned || chat.isArchived || isForked ? 'var(--space-2)' : 0
        }}>
          {chat.isPinned && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '2px',
              padding: '2px 6px',
              backgroundColor: 'var(--warning-100)',
              color: 'var(--warning-700)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.65rem',
              fontWeight: 600
            }}>
              <Pin size={10} /> Pinned
            </span>
          )}
          {chat.isArchived && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '2px',
              padding: '2px 6px',
              backgroundColor: 'var(--color-surface-secondary)',
              color: 'var(--color-text-tertiary)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.65rem',
              fontWeight: 600
            }}>
              <Archive size={10} /> Archived
            </span>
          )}
          {isForked && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '2px',
              padding: '2px 6px',
              backgroundColor: 'var(--primary-50)',
              color: 'var(--primary-600)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.65rem',
              fontWeight: 600
            }}>
              <GitFork size={10} /> Fork
            </span>
          )}
        </div>

        {/* Generated images preview grid */}
        {chat.generatedImages && chat.generatedImages.length > 0 ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: chat.generatedImages.length === 1 ? '1fr' : 'repeat(2, 1fr)',
            gap: '4px',
            marginBottom: 'var(--space-3)',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden'
          }}>
            {chat.generatedImages.slice(0, 4).map((img, idx) => (
              <div
                key={img.id || idx}
                style={{
                  position: 'relative',
                  paddingBottom: chat.generatedImages.length === 1 ? '56.25%' : '100%',
                  backgroundColor: 'var(--color-surface-secondary)'
                }}
              >
                <img
                  src={img.url}
                  alt={`Creative ${idx + 1}`}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                {/* Show +N overlay on last image if more than 4 */}
                {idx === 3 && chat.generatedImages.length > 4 && (
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '1.25rem',
                    fontWeight: 600
                  }}>
                    +{chat.generatedImages.length - 4}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : chat.thumbnail ? (
          <div style={{
            width: '100%',
            height: '120px',
            backgroundColor: 'var(--color-surface-secondary)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--space-3)',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <img
              src={chat.thumbnail}
              alt={chat.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        ) : (
          <div style={{
            width: '100%',
            height: '80px',
            backgroundColor: 'var(--color-surface-secondary)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--space-3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--color-text-tertiary)',
            fontSize: '0.75rem'
          }}>
            No creatives yet
          </div>
        )}

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 'var(--space-2)'
        }}>
          <h3 style={{
            margin: 0,
            fontSize: '1.125rem',
            fontWeight: 600,
            color: 'var(--color-text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1
          }}>
            {chat.title || 'Untitled Chat'}
          </h3>
          <div style={{ display: 'flex', gap: 'var(--space-1)', marginLeft: 'var(--space-2)', flexShrink: 0 }}>
            {/* Pin button */}
            <button
              onClick={(e) => handlePinClick(chat.id, e)}
              style={{
                padding: 'var(--space-1)',
                backgroundColor: 'transparent',
                color: chat.isPinned ? 'var(--warning-500)' : 'var(--color-text-tertiary)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s'
              }}
              title={chat.isPinned ? 'Unpin' : 'Pin to top'}
            >
              <Pin size={16} fill={chat.isPinned ? 'currentColor' : 'none'} />
            </button>
            {/* Archive button */}
            <button
              onClick={(e) => handleArchiveClick(chat.id, e)}
              style={{
                padding: 'var(--space-1)',
                backgroundColor: 'transparent',
                color: chat.isArchived ? 'var(--primary-500)' : 'var(--color-text-tertiary)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s'
              }}
              title={chat.isArchived ? 'Unarchive' : 'Archive'}
            >
              <Archive size={16} />
            </button>
            {/* Fork button */}
            <button
              onClick={(e) => handleForkClick(chat.id, e)}
              style={{
                padding: 'var(--space-1)',
                backgroundColor: 'transparent',
                color: 'var(--color-text-tertiary)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s'
              }}
              title="Fork this chat"
            >
              <GitFork size={16} />
            </button>
            {/* Delete button */}
            <button
              onClick={(e) => handleDeleteClick(chat.id, e)}
              style={{
                padding: 'var(--space-1)',
                backgroundColor: 'transparent',
                color: 'var(--color-text-tertiary)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s'
              }}
              title="Delete chat"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '0.875rem',
          color: 'var(--color-text-secondary)',
          marginBottom: 'var(--space-2)'
        }}>
          <span>{chat.brandName || 'No brand'}</span>
          <span>{formatDate(chat.updatedAt)}</span>
        </div>

        <div style={{
          display: 'flex',
          gap: 'var(--space-2)',
          fontSize: '0.75rem',
          color: 'var(--color-text-tertiary)',
          flexWrap: 'wrap'
        }}>
          <span style={{
            padding: 'var(--space-1) var(--space-2)',
            backgroundColor: 'var(--color-surface-secondary)',
            borderRadius: 'var(--radius-sm)'
          }}>
            {chat.messages?.length || 0} messages
          </span>
          {chat.generatedImages && chat.generatedImages.length > 0 && (
            <span style={{
              padding: 'var(--space-1) var(--space-2)',
              backgroundColor: 'var(--primary-100)',
              color: 'var(--primary-700)',
              borderRadius: 'var(--radius-sm)'
            }}>
              {chat.generatedImages.length} images
            </span>
          )}
          {hasForks && (
            <button
              onClick={(e) => handleShowForkTree(chat.id, e)}
              style={{
                padding: 'var(--space-1) var(--space-2)',
                backgroundColor: 'var(--primary-50)',
                color: 'var(--primary-600)',
                borderRadius: 'var(--radius-sm)',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.75rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}
              title="View fork tree"
            >
              <GitFork size={12} />
              {chat.childCount} fork{chat.childCount > 1 ? 's' : ''}
            </button>
          )}
        </div>

        {/* Tags */}
        {chat.tags && chat.tags.length > 0 && (
          <div style={{
            display: 'flex',
            gap: 'var(--space-1)',
            marginTop: 'var(--space-2)',
            flexWrap: 'wrap'
          }}>
            {chat.tags.slice(0, 3).map(tag => (
              <span
                key={tag}
                style={{
                  padding: '2px 6px',
                  backgroundColor: 'var(--color-surface-secondary)',
                  color: 'var(--color-text-secondary)',
                  borderRadius: 'var(--radius-full)',
                  fontSize: '0.65rem'
                }}
              >
                {tag}
              </span>
            ))}
            {chat.tags.length > 3 && (
              <span style={{
                padding: '2px 6px',
                color: 'var(--color-text-tertiary)',
                fontSize: '0.65rem'
              }}>
                +{chat.tags.length - 3}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render flat chat row (list view - no nesting)
  const renderFlatChatRow = (chat) => {
    const isForked = !!chat.forkedFromChatId;
    const hasForks = chat.childCount > 0;

    return (
      <div
        key={chat.id}
        onClick={() => handleChatClick(chat.id)}
        style={{
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-4)',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          backgroundColor: 'var(--color-surface)',
          display: 'flex',
          gap: 'var(--space-4)',
          alignItems: 'center',
          borderLeft: isForked ? '3px solid var(--primary-400)' : chat.isPinned ? '3px solid var(--warning-500)' : '1px solid var(--color-border)',
          marginBottom: 'var(--space-3)',
          opacity: chat.isArchived ? 0.7 : 1
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'var(--primary-400)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = isForked || chat.isPinned ? 'var(--primary-400)' : 'var(--color-border)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        {/* Generated images preview grid */}
        {chat.generatedImages && chat.generatedImages.length > 0 ? (
          <div style={{
            width: '100px',
            height: '100px',
            flexShrink: 0,
            display: 'grid',
            gridTemplateColumns: chat.generatedImages.length === 1 ? '1fr' : 'repeat(2, 1fr)',
            gap: '2px',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden'
          }}>
            {chat.generatedImages.slice(0, 4).map((img, idx) => (
              <div
                key={img.id || idx}
                style={{
                  position: 'relative',
                  backgroundColor: 'var(--color-surface-secondary)'
                }}
              >
                <img
                  src={img.url}
                  alt={`Creative ${idx + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                {/* Show +N overlay on last image if more than 4 */}
                {idx === 3 && chat.generatedImages.length > 4 && (
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '0.75rem',
                    fontWeight: 600
                  }}>
                    +{chat.generatedImages.length - 4}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : chat.thumbnail ? (
          <div style={{
            width: '100px',
            height: '100px',
            flexShrink: 0,
            backgroundColor: 'var(--color-surface-secondary)',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <img
              src={chat.thumbnail}
              alt={chat.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        ) : (
          <div style={{
            width: '100px',
            height: '100px',
            flexShrink: 0,
            backgroundColor: 'var(--color-surface-secondary)',
            borderRadius: 'var(--radius-md)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--color-text-tertiary)',
            fontSize: '0.65rem',
            textAlign: 'center',
            padding: 'var(--space-2)'
          }}>
            No creatives
          </div>
        )}

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Status badges */}
          <div style={{
            display: 'flex',
            gap: 'var(--space-1)',
            marginBottom: 'var(--space-1)'
          }}>
            {chat.isPinned && (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '2px',
                padding: '2px 6px',
                backgroundColor: 'var(--warning-100)',
                color: 'var(--warning-700)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.65rem',
                fontWeight: 600
              }}>
                <Pin size={10} /> Pinned
              </span>
            )}
            {chat.isArchived && (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '2px',
                padding: '2px 6px',
                backgroundColor: 'var(--color-surface-secondary)',
                color: 'var(--color-text-tertiary)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.65rem',
                fontWeight: 600
              }}>
                <Archive size={10} /> Archived
              </span>
            )}
            {isForked && (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '2px',
                padding: '2px 6px',
                backgroundColor: 'var(--primary-50)',
                color: 'var(--primary-600)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.65rem',
                fontWeight: 600
              }}>
                <GitFork size={10} /> Fork
              </span>
            )}
          </div>

          <h3 style={{
            margin: 0,
            fontSize: '1.125rem',
            fontWeight: 600,
            color: 'var(--color-text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            marginBottom: 'var(--space-1)'
          }}>
            {chat.title || 'Untitled Chat'}
          </h3>

          <div style={{
            fontSize: '0.875rem',
            color: 'var(--color-text-secondary)',
            marginBottom: 'var(--space-2)'
          }}>
            {chat.brandName || 'No brand'}
          </div>

          <div style={{
            display: 'flex',
            gap: 'var(--space-2)',
            flexWrap: 'wrap',
            fontSize: '0.75rem',
            color: 'var(--color-text-tertiary)'
          }}>
            <span style={{
              padding: 'var(--space-1) var(--space-2)',
              backgroundColor: 'var(--color-surface-secondary)',
              borderRadius: 'var(--radius-sm)'
            }}>
              {chat.messages?.length || 0} msgs
            </span>
            {chat.generatedImages && chat.generatedImages.length > 0 && (
              <span style={{
                padding: 'var(--space-1) var(--space-2)',
                backgroundColor: 'var(--primary-100)',
                color: 'var(--primary-700)',
                borderRadius: 'var(--radius-sm)'
              }}>
                {chat.generatedImages.length} imgs
              </span>
            )}
            {hasForks && (
              <button
                onClick={(e) => handleShowForkTree(chat.id, e)}
                style={{
                  padding: 'var(--space-1) var(--space-2)',
                  backgroundColor: 'var(--primary-50)',
                  color: 'var(--primary-600)',
                  borderRadius: 'var(--radius-sm)',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title="View fork tree"
              >
                <GitFork size={12} />
                {chat.childCount} fork{chat.childCount > 1 ? 's' : ''}
              </button>
            )}
            {chat.tags && chat.tags.length > 0 && (
              <span style={{
                padding: 'var(--space-1) var(--space-2)',
                backgroundColor: 'var(--color-surface-secondary)',
                borderRadius: 'var(--radius-sm)'
              }}>
                {chat.tags.length} tag{chat.tags.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={{
          display: 'flex',
          gap: 'var(--space-1)',
          flexShrink: 0
        }}>
          <button
            onClick={(e) => handlePinClick(chat.id, e)}
            style={{
              padding: 'var(--space-2)',
              backgroundColor: 'transparent',
              color: chat.isPinned ? 'var(--warning-500)' : 'var(--color-text-tertiary)',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer'
            }}
            title={chat.isPinned ? 'Unpin' : 'Pin'}
          >
            <Pin size={18} fill={chat.isPinned ? 'currentColor' : 'none'} />
          </button>
          <button
            onClick={(e) => handleArchiveClick(chat.id, e)}
            style={{
              padding: 'var(--space-2)',
              backgroundColor: 'transparent',
              color: chat.isArchived ? 'var(--primary-500)' : 'var(--color-text-tertiary)',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer'
            }}
            title={chat.isArchived ? 'Unarchive' : 'Archive'}
          >
            <Archive size={18} />
          </button>
          <button
            onClick={(e) => handleForkClick(chat.id, e)}
            style={{
              padding: 'var(--space-2)',
              backgroundColor: 'transparent',
              color: 'var(--color-text-tertiary)',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer'
            }}
            title="Fork"
          >
            <GitFork size={18} />
          </button>
          <button
            onClick={(e) => handleDeleteClick(chat.id, e)}
            style={{
              padding: 'var(--space-2)',
              backgroundColor: 'transparent',
              color: 'var(--color-text-tertiary)',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer'
            }}
            title="Delete"
          >
            <Trash2 size={18} />
          </button>
        </div>

        {/* Date */}
        <div style={{
          fontSize: '0.875rem',
          color: 'var(--color-text-secondary)',
          flexShrink: 0,
          textAlign: 'right',
          minWidth: '80px'
        }}>
          {formatDate(chat.updatedAt)}
        </div>
      </div>
    );
  };

  // Render a chat card (grid view)
  const renderChatCard = (chat, depth = 0) => {
    const hasChildren = chat.children && chat.children.length > 0;
    const isExpanded = expandedChats.has(chat.id);
    const isForked = depth > 0;
    const maxIndentDepth = 3; // Limit visual indentation
    const indentLevel = Math.min(depth, maxIndentDepth);

    return (
      <div key={chat.id} style={{ marginLeft: `${indentLevel * 24}px` }}>
        <div
          onClick={() => handleChatClick(chat.id)}
          style={{
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-4)',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            backgroundColor: 'var(--color-surface)',
            overflow: 'hidden',
            position: 'relative',
            borderLeft: isForked ? '3px solid var(--primary-400)' : '1px solid var(--color-border)',
            marginBottom: 'var(--space-3)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--primary-400)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = isForked ? 'var(--primary-400)' : 'var(--color-border)';
            e.currentTarget.style.borderLeftColor = isForked ? 'var(--primary-400)' : 'var(--color-border)';
            e.currentTarget.style.boxShadow = 'none';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          {/* Expand/collapse toggle for chats with children */}
          {hasChildren && (
            <button
              onClick={(e) => toggleExpand(chat.id, e)}
              style={{
                position: 'absolute',
                top: 'var(--space-2)',
                left: 'var(--space-2)',
                width: '24px',
                height: '24px',
                borderRadius: 'var(--radius-sm)',
                border: 'none',
                background: 'var(--color-surface-secondary)',
                color: 'var(--color-text-secondary)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                zIndex: 5
              }}
              title={isExpanded ? 'Collapse forks' : 'Show forks'}
            >
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          )}

          {/* Fork indicator for forked chats */}
          {isForked && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
              fontSize: '0.7rem',
              color: 'var(--primary-600)',
              marginBottom: 'var(--space-2)',
              marginLeft: hasChildren ? '28px' : '0'
            }}>
              <GitFork size={12} />
              <span>Forked at message {(chat.forkedAtMessageIndex ?? 0) + 1}</span>
            </div>
          )}

          {chat.thumbnail && (
            <div style={{
              width: '100%',
              height: '180px',
              backgroundColor: 'var(--color-surface-secondary)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--space-3)',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img
                src={chat.thumbnail}
                alt={chat.title}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                onError={(e) => {
                  console.error('Failed to load thumbnail:', chat.thumbnail);
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<div style="color: var(--color-text-tertiary); font-size: 0.875rem;">No preview</div>';
                }}
                onLoad={() => {
                  console.log('Successfully loaded thumbnail for chat:', chat.id);
                }}
              />
            </div>
          )}

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 'var(--space-2)'
          }}>
            <h3 style={{
              margin: 0,
              fontSize: '1.125rem',
              fontWeight: 600,
              color: 'var(--color-text-primary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1
            }}>
              {chat.title || 'Untitled Chat'}
            </h3>
            <div style={{ display: 'flex', gap: 'var(--space-1)', marginLeft: 'var(--space-2)', flexShrink: 0 }}>
              {/* Fork button */}
              <button
                onClick={(e) => handleForkClick(chat.id, e)}
                style={{
                  padding: 'var(--space-1)',
                  backgroundColor: 'transparent',
                  color: 'var(--color-text-tertiary)',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--primary-100)';
                  e.currentTarget.style.color = 'var(--primary-600)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'var(--color-text-tertiary)';
                }}
                title="Fork this chat"
              >
                <GitFork size={16} />
              </button>
              {/* Delete button */}
              <button
                onClick={(e) => handleDeleteClick(chat.id, e)}
                style={{
                  padding: 'var(--space-1)',
                  backgroundColor: 'transparent',
                  color: 'var(--color-text-tertiary)',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--error-50)';
                  e.currentTarget.style.color = 'var(--error-600)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'var(--color-text-tertiary)';
                }}
                title="Delete chat"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '0.875rem',
            color: 'var(--color-text-secondary)',
            marginBottom: 'var(--space-2)'
          }}>
            <span>{chat.brandName || 'No brand'}</span>
            <span>{formatDate(chat.updatedAt)}</span>
          </div>

          <div style={{
            display: 'flex',
            gap: 'var(--space-2)',
            fontSize: '0.75rem',
            color: 'var(--color-text-tertiary)',
            flexWrap: 'wrap'
          }}>
            <span style={{
              padding: 'var(--space-1) var(--space-2)',
              backgroundColor: 'var(--color-surface-secondary)',
              borderRadius: 'var(--radius-sm)'
            }}>
              {chat.messages?.length || 0} messages
            </span>
            {chat.generatedImages && chat.generatedImages.length > 0 && (
              <span style={{
                padding: 'var(--space-1) var(--space-2)',
                backgroundColor: 'var(--primary-100)',
                color: 'var(--primary-700)',
                borderRadius: 'var(--radius-sm)'
              }}>
                {chat.generatedImages.length} images
              </span>
            )}
            <span style={{
              padding: 'var(--space-1) var(--space-2)',
              backgroundColor: 'var(--color-surface-secondary)',
              borderRadius: 'var(--radius-sm)',
              textTransform: 'capitalize'
            }}>
              {chat.currentPhase || 'N/A'}
            </span>
            {hasChildren && (
              <span style={{
                padding: 'var(--space-1) var(--space-2)',
                backgroundColor: 'var(--primary-50)',
                color: 'var(--primary-600)',
                borderRadius: 'var(--radius-sm)'
              }}>
                {chat.children.length} fork{chat.children.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        {/* Render children if expanded */}
        {hasChildren && isExpanded && (
          <div style={{ marginTop: 'var(--space-2)' }}>
            {chat.children.map(child => renderChatCard(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  // Render a chat row (list view)
  const renderChatRow = (chat, depth = 0) => {
    const hasChildren = chat.children && chat.children.length > 0;
    const isExpanded = expandedChats.has(chat.id);
    const isForked = depth > 0;
    const maxIndentDepth = 3;
    const indentLevel = Math.min(depth, maxIndentDepth);

    return (
      <div key={chat.id} style={{ marginLeft: `${indentLevel * 24}px` }}>
        <div
          onClick={() => handleChatClick(chat.id)}
          style={{
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-4)',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            backgroundColor: 'var(--color-surface)',
            display: 'flex',
            gap: 'var(--space-4)',
            alignItems: 'center',
            borderLeft: isForked ? '3px solid var(--primary-400)' : '1px solid var(--color-border)',
            marginBottom: 'var(--space-3)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--primary-400)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = isForked ? 'var(--primary-400)' : 'var(--color-border)';
            e.currentTarget.style.borderLeftColor = isForked ? 'var(--primary-400)' : 'var(--color-border)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          {/* Expand/collapse toggle */}
          {hasChildren && (
            <button
              onClick={(e) => toggleExpand(chat.id, e)}
              style={{
                width: '24px',
                height: '24px',
                borderRadius: 'var(--radius-sm)',
                border: 'none',
                background: 'var(--color-surface-secondary)',
                color: 'var(--color-text-secondary)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                flexShrink: 0
              }}
              title={isExpanded ? 'Collapse forks' : 'Show forks'}
            >
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          )}

          {/* Thumbnail */}
          {chat.thumbnail && (
            <div style={{
              width: '120px',
              height: '120px',
              flexShrink: 0,
              backgroundColor: 'var(--color-surface-secondary)',
              borderRadius: 'var(--radius-md)',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img
                src={chat.thumbnail}
                alt={chat.title}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                onError={(e) => {
                  console.error('Failed to load thumbnail:', chat.thumbnail);
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<div style="color: var(--color-text-tertiary); font-size: 0.75rem;">No preview</div>';
                }}
              />
            </div>
          )}

          {/* Content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Fork indicator */}
            {isForked && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-1)',
                fontSize: '0.7rem',
                color: 'var(--primary-600)',
                marginBottom: 'var(--space-1)'
              }}>
                <GitFork size={12} />
                <span>Forked at message {(chat.forkedAtMessageIndex ?? 0) + 1}</span>
              </div>
            )}

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: 'var(--space-2)'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.25rem',
                fontWeight: 600,
                color: 'var(--color-text-primary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1
              }}>
                {chat.title || 'Untitled Chat'}
              </h3>
              <div style={{ display: 'flex', gap: 'var(--space-1)', marginLeft: 'var(--space-2)', flexShrink: 0 }}>
                {/* Fork button */}
                <button
                  onClick={(e) => handleForkClick(chat.id, e)}
                  style={{
                    padding: 'var(--space-1)',
                    backgroundColor: 'transparent',
                    color: 'var(--color-text-tertiary)',
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--primary-100)';
                    e.currentTarget.style.color = 'var(--primary-600)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = 'var(--color-text-tertiary)';
                  }}
                  title="Fork this chat"
                >
                  <GitFork size={18} />
                </button>
                {/* Delete button */}
                <button
                  onClick={(e) => handleDeleteClick(chat.id, e)}
                  style={{
                    padding: 'var(--space-1)',
                    backgroundColor: 'transparent',
                    color: 'var(--color-text-tertiary)',
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--error-50)';
                    e.currentTarget.style.color = 'var(--error-600)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = 'var(--color-text-tertiary)';
                  }}
                  title="Delete chat"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>

            <div style={{
              fontSize: '0.875rem',
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--space-2)'
            }}>
              {chat.brandName || 'No brand'}
            </div>

            <div style={{
              display: 'flex',
              gap: 'var(--space-2)',
              flexWrap: 'wrap',
              fontSize: '0.75rem',
              color: 'var(--color-text-tertiary)'
            }}>
              <span style={{
                padding: 'var(--space-1) var(--space-2)',
                backgroundColor: 'var(--color-surface-secondary)',
                borderRadius: 'var(--radius-sm)'
              }}>
                {chat.messages?.length || 0} messages
              </span>
              {chat.generatedImages && chat.generatedImages.length > 0 && (
                <span style={{
                  padding: 'var(--space-1) var(--space-2)',
                  backgroundColor: 'var(--primary-100)',
                  color: 'var(--primary-700)',
                  borderRadius: 'var(--radius-sm)'
                }}>
                  {chat.generatedImages.length} images
                </span>
              )}
              <span style={{
                padding: 'var(--space-1) var(--space-2)',
                backgroundColor: 'var(--color-surface-secondary)',
                borderRadius: 'var(--radius-sm)',
                textTransform: 'capitalize'
              }}>
                {chat.currentPhase || 'N/A'}
              </span>
              {hasChildren && (
                <span style={{
                  padding: 'var(--space-1) var(--space-2)',
                  backgroundColor: 'var(--primary-50)',
                  color: 'var(--primary-600)',
                  borderRadius: 'var(--radius-sm)'
                }}>
                  {chat.children.length} fork{chat.children.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>

          {/* Date */}
          <div style={{
            fontSize: '0.875rem',
            color: 'var(--color-text-secondary)',
            flexShrink: 0,
            textAlign: 'right'
          }}>
            {formatDate(chat.updatedAt)}
          </div>
        </div>

        {/* Render children if expanded */}
        {hasChildren && isExpanded && (
          <div style={{ marginTop: 'var(--space-2)' }}>
            {chat.children.map(child => renderChatRow(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (!currentUser) {
    return (
      <div style={{ padding: 'var(--space-8)', textAlign: 'center' }}>
        <h2>Please sign in to view your saved chats</h2>
      </div>
    );
  }

  return (
    <div style={{
      padding: 'var(--space-8)',
      maxWidth: '1200px',
      margin: '0 auto',
      minHeight: '100vh'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 'var(--space-4)'
      }}>
        <h1 style={{
          margin: 0,
          color: 'var(--color-text-primary)'
        }}>Creative Chats</h1>
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
          {/* View Toggle */}
          <div style={{
            display: 'flex',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
            backgroundColor: 'var(--color-surface)'
          }}>
            <button
              onClick={() => setViewMode('masonry')}
              style={{
                padding: 'var(--space-2) var(--space-3)',
                backgroundColor: viewMode === 'masonry' ? 'var(--primary-100)' : 'transparent',
                color: viewMode === 'masonry' ? 'var(--primary-700)' : 'var(--color-text-secondary)',
                border: 'none',
                borderRight: '1px solid var(--color-border)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                transition: 'all 0.2s'
              }}
            >
              <LayoutGrid size={18} />
              <span>Grid</span>
            </button>
            <button
              onClick={() => setViewMode('list')}
              style={{
                padding: 'var(--space-2) var(--space-3)',
                backgroundColor: viewMode === 'list' ? 'var(--primary-100)' : 'transparent',
                color: viewMode === 'list' ? 'var(--primary-700)' : 'var(--color-text-secondary)',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                transition: 'all 0.2s'
              }}
            >
              <List size={18} />
              <span>List</span>
            </button>
          </div>

          <button
            onClick={handleNewChat}
            style={{
              padding: 'var(--space-3) var(--space-5)',
              backgroundColor: 'var(--primary-600)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 500,
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--primary-700)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--primary-600)'}
          >
            + New Chat
          </button>
        </div>
      </div>

      {/* Filters */}
      {!isLoading && chats.length > 0 && (
        <ChatFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          sortBy={sortBy}
          onSortChange={setSortBy}
          forkDepthFilter={forkDepthFilter}
          onForkDepthChange={setForkDepthFilter}
          showArchived={showArchived}
          onShowArchivedChange={setShowArchived}
          showPinnedOnly={showPinnedOnly}
          onShowPinnedOnlyChange={setShowPinnedOnly}
          selectedTags={selectedTags}
          onTagsChange={setSelectedTags}
          availableTags={availableTags}
          totalCount={flatChats.length}
          filteredCount={filteredChats.length}
        />
      )}

      {isLoading && (
        <div style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--color-text-secondary)' }}>
          <div className="loading-spinner">Loading...</div>
        </div>
      )}

      {error && (
        <div style={{
          padding: 'var(--space-4)',
          backgroundColor: 'var(--error-50)',
          color: 'var(--error-700)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--space-4)'
        }}>
          {error}
        </div>
      )}

      {!isLoading && !error && chats.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: 'var(--space-8)',
          backgroundColor: 'var(--color-surface-secondary)',
          borderRadius: 'var(--radius-lg)',
          border: '2px dashed var(--color-border)'
        }}>
          <h3 style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-3)' }}>
            No saved chats yet
          </h3>
          <p style={{ color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-5)' }}>
            Start a conversation to create your first ad creative
          </p>
          <button
            onClick={handleNewChat}
            style={{
              padding: 'var(--space-3) var(--space-5)',
              backgroundColor: 'var(--primary-600)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 500,
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--primary-700)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--primary-600)'}
          >
            Start New Chat
          </button>
        </div>
      )}

      {/* Grid View - Flat filtered list */}
      {!isLoading && !error && filteredChats.length > 0 && viewMode === 'masonry' && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: 'var(--space-4)',
          alignItems: 'start'
        }}>
          {filteredChats.map(chat => renderFlatChatCard(chat))}
        </div>
      )}

      {/* List View - Flat filtered list */}
      {!isLoading && !error && filteredChats.length > 0 && viewMode === 'list' && (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {filteredChats.map(chat => renderFlatChatRow(chat))}
        </div>
      )}

      {/* No results after filtering */}
      {!isLoading && !error && chats.length > 0 && filteredChats.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: 'var(--space-8)',
          backgroundColor: 'var(--color-surface-secondary)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--color-border)'
        }}>
          <h3 style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-3)' }}>
            No chats match your filters
          </h3>
          <p style={{ color: 'var(--color-text-tertiary)' }}>
            Try adjusting your search or filter criteria
          </p>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={chatToDelete !== null}
        onClose={() => setChatToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete Chat?"
        message="Are you sure you want to delete this chat? This action cannot be undone and all messages and generated images will be permanently removed."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Fork Tree Modal */}
      <ForkTreeModal
        isOpen={forkTreeChat !== null}
        onClose={() => setForkTreeChat(null)}
        chatTree={forkTreeChat}
        onNavigateToChat={(chatId) => {
          setForkTreeChat(null);
          handleChatClick(chatId);
        }}
      />
    </div>
  );
}
